import { useEffect, useRef, useMemo } from 'react';
import { useCubeQuery as useOriginalCubeQuery } from '@cubejs-client/react';
import { useRollbar } from '@rollbar/react';
import { notification } from 'antd';

// Global state for loading count
let activeQueries = 0;
const LOADING_KEY = 'global_cube_loading';

const updateLoading = (delta) => {
    activeQueries += delta;

    if (activeQueries > 0) {
        if (activeQueries === 1 && delta === 1) {
            notification.info({
                key: LOADING_KEY,
                message: 'Loading reporting data',
                duration: 0,
                placement: 'top',
            });
        }
    } else {
        activeQueries = 0;
        notification.destroy(LOADING_KEY);
    }
};

export const useCubeQuery = (query, options = {}, ...rest) => {
    // @cubejs-client/react v1.x renamed the `cubejsApi` option to `cubeApi`.
    // Accept both so existing call sites keep working.
    const normalizedOptions = options && options.cubejsApi && !options.cubeApi
        ? { ...options, cubeApi: options.cubejsApi }
        : options;
    const result = useOriginalCubeQuery(query, normalizedOptions, ...rest);
    const rollbar = useRollbar();
    
    // Ref to track if we contributed to the loading count
    // Use a ref that persists across renders to track "our" contribution to the global count
    const hasContributedToLoading = useRef(false);

    useEffect(() => {
        // If query is loading (and we haven't already incremented), increment global count
        if (result.isLoading && !hasContributedToLoading.current) {
            updateLoading(1);
            hasContributedToLoading.current = true;
        } 
        // If query stopped loading (and we previously incremented), decrement
        else if (!result.isLoading && hasContributedToLoading.current) {
            updateLoading(-1);
            hasContributedToLoading.current = false;
        }

        // Cleanup: on unmount, if we are currently contributing to the loading count, decrement it.
        return () => {
             // We use a ref inside the cleanup to know if THIS effect instance needs cleanup?
             // Actually, the safest way with effects is strict symmetry.
             // If we unmount, and hasContributedToLoading is true, we simply decrement.
             // But verify if the dependency change triggered this.
        };
    }, [result.isLoading]);
    
    // Explicit cleanup effect for unmount
    useEffect(() => {
        return () => {
            if (hasContributedToLoading.current) {
                updateLoading(-1);
                hasContributedToLoading.current = false;
            }
        };
    }, []);

    useEffect(() => {
        if (result.error) {
            rollbar.critical('[Cube.js error]', result.error);
            notification.error({
                message: 'Error loading reporting data',
            });
        }
    }, [result.error, rollbar]);

    return result;
};
